/**
 * EmailComposer — editable subject + body + (Review-only) return-by date,
 * with a non-editable locked footer preview underneath.
 *
 * Used inside ActionConfirmModal for both the Reject action (writes
 * reviewNotes that the server interpolates into the rejection email body)
 * and the Review action (writes the full review email subject + body sent
 * via /review-email). Both flows show the locked footer so staff see the
 * full message they're sending — the footer text is rendered server-side
 * but mirrored here as a preview so there are no surprises.
 *
 * Phase 5d-1 (Decision 5-28 + ADR-029 §2 + Channel Partner Ref v5 §5.4).
 */
import { formatDate } from '../../lib/time-format';

export interface EmailComposerProps {
  variant: 'reject' | 'review';
  subject: string;
  body: string;
  returnByDate?: string;
  applicantName: string;
  disabled?: boolean;
  onSubjectChange?: (subject: string) => void;
  onBodyChange: (body: string) => void;
  onReturnByDateChange?: (date: string) => void;
}

export function EmailComposer({
  variant,
  subject,
  body,
  returnByDate,
  applicantName,
  disabled,
  onSubjectChange,
  onBodyChange,
  onReturnByDateChange,
}: EmailComposerProps) {
  const lockedFooter =
    variant === 'review'
      ? buildReviewFooterPreview(returnByDate)
      : buildRejectFooterPreview();

  return (
    <div className="portal-email-composer">
      {variant === 'review' && onSubjectChange && (
        <label className="portal-email-composer-field">
          <span>Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            maxLength={200}
            disabled={disabled}
            required
          />
        </label>
      )}
      <label className="portal-email-composer-field">
        <span>{variant === 'review' ? 'Message body' : 'Reviewer notes (will be sent to the applicant)'}</span>
        <textarea
          rows={6}
          value={body}
          onChange={(e) => onBodyChange(e.target.value)}
          maxLength={variant === 'review' ? 8000 : 2000}
          disabled={disabled}
          required
        />
        <small className="portal-email-composer-help">
          You can edit the body but not the locked footer below. Plain-text emails are sent in addition to the HTML version.
        </small>
      </label>
      {variant === 'review' && onReturnByDateChange && (
        <label className="portal-email-composer-field portal-email-composer-field-inline">
          <span>Respond by (optional)</span>
          <input
            type="date"
            value={returnByDate ? returnByDate.slice(0, 10) : ''}
            onChange={(e) =>
              onReturnByDateChange(
                e.target.value ? new Date(e.target.value).toISOString() : '',
              )
            }
            disabled={disabled}
          />
          <small className="portal-email-composer-help">
            If set, the locked footer asks the applicant to reply by this date.
          </small>
        </label>
      )}
      <fieldset className="portal-email-composer-locked" disabled>
        <legend>Locked footer (preview)</legend>
        <p className="portal-email-composer-greeting">Hi {applicantName.trim() || 'there'},</p>
        <p className="portal-email-composer-preview-body">{body || `(your ${variant === 'review' ? 'message' : 'reviewer notes'} appears here)`}</p>
        <p className="portal-email-composer-footer">{lockedFooter}</p>
        <p className="portal-email-composer-signoff">— The Nexanomics team</p>
      </fieldset>
    </div>
  );
}

function buildReviewFooterPreview(returnByDate?: string): string {
  // Mirrors renderReviewFooter() in lambdas/admin-api/src/email.ts. Drift
  // here is operationally invisible to the user but visible to whoever
  // edits one of the two — the test suite exercises both sides so a
  // disagreement surfaces.
  if (returnByDate) {
    const formatted = formatHumanDate(returnByDate);
    return `Your application is held as "Pending Response" while we await your reply. Please respond by ${formatted} so we can keep your application moving.`;
  }
  return 'Your application is held as "Pending Response" while we await your reply.';
}

function buildRejectFooterPreview(): string {
  return "If your circumstances change, you're welcome to apply again in the future.";
}

function formatHumanDate(iso: string): string {
  // The reply-by is the DAY staff picked (sent as that day's UTC midnight) — written as picked, in
  // every zone, the way the partner email reads it. Never shifted by the viewer's offset.
  const day = /^\d{4}-\d{2}-\d{2}/.exec(iso)?.[0];
  return day ? formatDate(day, 'full') : iso;
}
